import { useEffect, useState, useRef } from "react";import axios from "axios";
import { BadgePercent, Plus, Trash2, Pencil, X, Check, Upload, Loader2, Tag, Link2, KeyRound, BarChart3, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { PRO_CATEGORY_ORDER } from "../data/proCategories";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const RED = "#FF3B30";

const fileUrl = (v) => (!v ? "" : v.startsWith("http") ? v : `${API}/files/${v.replace(/^\/api\/files\//, "")}`);

const emptyForm = { service_icon: "", service_name: "", service_desc: "", promo_desc: "", category: "Altro", discount_pct: "", discount_label: "", promo_type: "link", url: "", code: "", code_usage_url: "", maps_url: "" };

const pillOf = (p) => (p.discount_label ? p.discount_label : (p.discount_pct ? `-${p.discount_pct}%` : null));

const PromoForm = ({ initial, onCancel, onSave, uploadFn, allowMaps }) => {
  const [f, setF] = useState({ ...emptyForm, ...initial });
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFn(file);
      set("service_icon", url);
    } catch { toast.error("Errore caricamento immagine"); }
    setUploading(false);
  };

  const submit = () => {
    if (!f.service_name.trim()) return toast.error("Inserisci il nome del servizio");
    if (f.promo_type === "link" && !f.url.trim()) return toast.error("Inserisci il link della promo");
    if (f.promo_type === "code" && !f.code.trim()) return toast.error("Inserisci il codice sconto");
    onSave(f);
  };

  const input = "w-full border border-zinc-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10";

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="promo-form">
      <div className="flex items-center gap-3">
        <label className="shrink-0 w-16 h-16 rounded-2xl bg-zinc-100 overflow-hidden flex items-center justify-center cursor-pointer relative">
          {f.service_icon ? <img src={fileUrl(f.service_icon)} alt="" className="w-full h-full object-cover" /> : (uploading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-400" /> : <Upload className="w-5 h-5 text-zinc-400" />)}
          <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="promo-icon-upload" />
        </label>
        <input className={input} placeholder="Nome servizio" value={f.service_name} onChange={(e) => set("service_name", e.target.value)} data-testid="promo-field-name" />
      </div>
      <textarea className={input} rows={2} placeholder="Descrizione servizio" value={f.service_desc} onChange={(e) => set("service_desc", e.target.value)} data-testid="promo-field-servicedesc" />
      <input className={input} placeholder="Descrizione promo (es. 10% sul biglietto)" value={f.promo_desc} onChange={(e) => set("promo_desc", e.target.value)} data-testid="promo-field-promodesc" />

      <div>
        <label className="text-[11px] font-bold text-zinc-500">Categoria</label>
        <select className={input} value={f.category || "Altro"} onChange={(e) => set("category", e.target.value)} data-testid="promo-field-category">
          {PRO_CATEGORY_ORDER.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-bold text-zinc-500">% Sconto</label>
          <input type="number" min="0" max="100" className={input} placeholder="10" value={f.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} data-testid="promo-field-pct" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-zinc-500">Etichetta (priorità)</label>
          <input maxLength={12} className={input} placeholder="2x1, omaggio…" value={f.discount_label} onChange={(e) => set("discount_label", e.target.value)} data-testid="promo-field-label" />
        </div>
      </div>

      <div className="flex gap-2">
        {["link", "code"].map((tp) => (
          <button key={tp} onClick={() => set("promo_type", tp)} className={`flex-1 py-2 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-1.5 border ${f.promo_type === tp ? "text-white border-transparent" : "bg-white text-zinc-600 border-zinc-200"}`} style={f.promo_type === tp ? { background: RED } : {}} data-testid={`promo-type-${tp}`}>
            {tp === "link" ? <Link2 className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />} {tp === "link" ? "Link" : "Codice sconto"}
          </button>
        ))}
      </div>

      {f.promo_type === "link" ? (
        <input className={input} placeholder="https://link-partner.it/..." value={f.url} onChange={(e) => set("url", e.target.value)} data-testid="promo-field-url" />
      ) : (
        <div className="space-y-2">
          <input className={input} placeholder="Codice sconto (es. HOST10)" value={f.code} onChange={(e) => set("code", e.target.value)} data-testid="promo-field-code" />
          <input className={input} placeholder="Link 'dove usarlo' (facoltativo)" value={f.code_usage_url} onChange={(e) => set("code_usage_url", e.target.value)} data-testid="promo-field-codeurl" />
        </div>
      )}

      {allowMaps && (
        <div>
          <input className={input} placeholder="Link Google Maps del servizio (obbligatorio)" value={f.maps_url} onChange={(e) => set("maps_url", e.target.value)} data-testid="promo-field-maps" />
          <p className="text-[11px] text-zinc-400 mt-1 px-1">Obbligatorio: serve per mostrare lo sconto sulla mappa. Apri il luogo su Google Maps → Condividi → copia il link.</p>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl bg-zinc-100 text-zinc-700 font-bold text-sm">Annulla</button>
        <button onClick={submit} className="flex-1 py-2.5 rounded-xl text-white font-bold text-sm inline-flex items-center justify-center gap-1.5" style={{ background: RED }} data-testid="promo-save-btn"><Check className="w-4 h-4" /> Salva</button>
      </div>
    </div>
  );
};

const MiniPill = ({ p }) => {
  const pill = p.pill || pillOf(p);
  if (!pill) return null;
  return <span className="px-2 py-0.5 rounded-full text-white text-[10px] font-black shrink-0" style={{ background: RED }}>{pill}</span>;
};

const DragHandle = ({ controls }) => (
  <button
    type="button"
    onPointerDown={(e) => { e.preventDefault(); controls.start(e); }}
    className="shrink-0 -ml-1 p-1 text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing touch-none"
    aria-label="Trascina per riordinare"
    data-testid="promo-drag-handle"
  >
    <GripVertical className="w-5 h-5" />
  </button>
);

const HostlandRow = ({ p, transparency_text, onToggle, fileUrl }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} className="list-none">
      <div className="rounded-2xl border border-zinc-200 bg-white p-3" data-testid={`hostland-promo-${p.promo_id}`}>
        <div className="flex items-center gap-2">
          <DragHandle controls={controls} />
          <div className="w-11 h-11 rounded-xl bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
            {p.service_icon ? <img src={fileUrl(p.service_icon)} alt="" className="w-full h-full object-cover" /> : <Tag className="w-5 h-5 text-zinc-300" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap"><span className="font-bold text-sm text-zinc-900 truncate">{p.service_name}</span><MiniPill p={p} /></div>
            <p className="text-xs text-zinc-500 line-clamp-2 break-words">{p.promo_desc || p.service_desc}</p>
          </div>
          <button onClick={() => onToggle(p.promo_id, !p.enabled)} className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${p.enabled ? "" : "bg-zinc-200"}`} style={p.enabled ? { background: RED } : {}} data-testid={`hostland-toggle-${p.promo_id}`}>
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${p.enabled ? "left-6" : "left-1"}`} />
          </button>
        </div>
        <p className="text-[10px] text-zinc-400 leading-snug mt-2 border-t border-zinc-100 pt-2">{transparency_text}</p>
      </div>
    </Reorder.Item>
  );
};

const LocalRow = ({ p, onEdit, onDelete, fileUrl }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} className="list-none">
      <div className="rounded-2xl border border-zinc-200 bg-white p-3 flex items-center gap-2" data-testid={`local-promo-${p.promo_id}`}>
        <DragHandle controls={controls} />
        <div className="w-10 h-10 rounded-xl bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
          {p.service_icon ? <img src={fileUrl(p.service_icon)} alt="" className="w-full h-full object-cover" /> : <Tag className="w-5 h-5 text-zinc-300" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0 flex-wrap"><span className="font-bold text-sm truncate">{p.service_name}</span><MiniPill p={p} /></div>
          <p className="text-xs text-zinc-500 line-clamp-2 break-words">{p.promo_desc}</p>
        </div>
        <button onClick={() => onEdit(p)} className="p-2 text-zinc-400 hover:text-zinc-900" data-testid={`edit-local-${p.promo_id}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onDelete(p.promo_id)} className="p-2 text-zinc-400 hover:text-red-500" data-testid={`del-local-${p.promo_id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </Reorder.Item>
  );
};

const HostPromosSection = ({ propertyId, token, variant = "all" }) => {
  const showHostland = variant === "all" || variant === "hostland";
  const showLocal = variant === "all" || variant === "local";
  const [data, setData] = useState({ hostland: [], local: [], transparency_text: "" });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/promos/host/${propertyId}`, { headers })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [propertyId]);

  const uploadFn = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await axios.post(`${API}/guestbook/${propertyId}/upload?max_dim=512`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
    return r.data.file_id;
  };

  const toggleHostland = async (promo_id, enabled) => {
    setData((d) => ({ ...d, hostland: d.hostland.map((p) => p.promo_id === promo_id ? { ...p, enabled } : p) }));
    try { await axios.put(`${API}/promos/host/${propertyId}/hostland-toggle`, { promo_id, enabled }, { headers }); }
    catch { toast.error("Errore"); load(); }
  };

  const saveLocal = async (form) => {
    try {
      if (editing) await axios.put(`${API}/promos/host/${propertyId}/local/${editing.promo_id}`, form, { headers });
      else await axios.post(`${API}/promos/host/${propertyId}/local`, form, { headers });
      toast.success("Promo salvata"); setShowForm(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const delLocal = async (promo_id) => {
    if (!window.confirm("Eliminare questa promo?")) return;
    try { await axios.delete(`${API}/promos/host/${propertyId}/local/${promo_id}`, { headers }); toast.success("Eliminata"); load(); }
    catch { toast.error("Errore"); }
  };

  // ── Drag & drop: riordino con salvataggio automatico (debounce) ──
  const saveTimer = useRef(null);
  const scheduleSave = (endpoint, ids) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await axios.put(`${API}/promos/host/${propertyId}/${endpoint}`, { order: ids }, { headers }); }
      catch { toast.error("Ordine non salvato"); }
    }, 600);
  };
  const reorderHostland = (list) => {
    setData((d) => ({ ...d, hostland: list }));
    scheduleSave("hostland-reorder", list.map((p) => p.promo_id));
  };
  const reorderLocal = (list) => {
    setData((d) => ({ ...d, local: list }));
    scheduleSave("local/reorder", list.map((p) => p.promo_id));
  };

  if (loading) return <div className="py-10 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid={variant === "all" ? "host-promos-section" : `host-promos-section-${variant}`}>
      {/* A — Promo Hostland */}
      {showHostland && (
      <div>
        <h3 className="text-sm font-black text-zinc-900 mb-1 flex items-center gap-2"><BadgePercent className="w-4 h-4" style={{ color: RED }} /> Promo Hostland disponibili</h3>
        <p className="text-xs text-zinc-500 mb-3">Gli sconti dei partner di Vita da Host sono <span className="font-bold text-zinc-700">già attivi di default</span>. Se sai che alcuni servizi non sono presenti nella tua zona, disattivali con l'interruttore.</p>
        {data.hostland.length === 0 ? (
          <p className="text-xs text-zinc-400 italic">Nessuna promo Hostland al momento.</p>
        ) : (
          <Reorder.Group axis="y" values={data.hostland} onReorder={reorderHostland} className="space-y-3">
            {data.hostland.map((p) => (
              <HostlandRow key={p.promo_id} p={p} transparency_text={data.transparency_text} onToggle={toggleHostland} fileUrl={fileUrl} />
            ))}
          </Reorder.Group>
        )}
      </div>
      )}

      {/* B — Promo locali host */}
      {showLocal && (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-black text-zinc-900 flex items-center gap-2"><Tag className="w-4 h-4" style={{ color: RED }} /> Le tue promo locali</h3>
          {!showForm && <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-3 py-1.5 rounded-full text-white text-xs font-bold inline-flex items-center gap-1" style={{ background: RED }} data-testid="add-local-promo-btn"><Plus className="w-4 h-4" /> Aggiungi</button>}
        </div>

        {showForm && <PromoForm initial={editing || {}} allowMaps onCancel={() => { setShowForm(false); setEditing(null); }} onSave={saveLocal} uploadFn={uploadFn} />}

        {!showForm && (
          data.local.length === 0 ? (
            <p className="text-xs text-zinc-400 italic">Nessuna promo locale. Aggiungi sconti dei tuoi partner locali (musei, noleggi, ristoranti…).</p>
          ) : (
            <Reorder.Group axis="y" values={data.local} onReorder={reorderLocal} className="space-y-2 mt-2">
              {data.local.map((p) => (
                <LocalRow key={p.promo_id} p={p} onEdit={(pp) => { setEditing(pp); setShowForm(true); }} onDelete={delLocal} fileUrl={fileUrl} />
              ))}
            </Reorder.Group>
          )
        )}
      </div>
      )}

      {/* Disclaimer analytics aperture sconti */}
      {showLocal && (
      <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-3 flex items-start gap-2.5" data-testid="promo-analytics-disclaimer">
        <BarChart3 className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-zinc-500 leading-snug">
          I dati sulle <span className="font-bold text-zinc-700">aperture degli sconti da parte degli ospiti</span> (quale sconto e quante volte è stato aperto) li trovi nella sezione <span className="font-bold text-zinc-700">Statistiche</span> del tuo pannello.
        </p>
      </div>
      )}
    </div>
  );
};

export { PromoForm, pillOf, MiniPill };
export default HostPromosSection;
